import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAppSelector } from '@app/hooks';
import { RootState } from '@app/store';
import { HomeHealth } from '@features/home-health/types';
import { Property } from '@features/home/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';

// Fix default icon paths broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const HEALTH_UNKNOWN_COLOR = '#9e9e9e';
const HEALTH_GOOD_COLOR = '#4caf50';
const HEALTH_BAD_COLOR = '#f44336';

const markerColor = (health: HomeHealth | null | undefined): string => {
  if (!health) return HEALTH_UNKNOWN_COLOR;
  return health.all_healthy ? HEALTH_GOOD_COLOR : HEALTH_BAD_COLOR;
};

/** Recenter the map whenever the set of homes changes */
const AutoBounds = ({ homes }: { homes: Property[] }) => {
  const map = useMap();
  useEffect(() => {
    const coords = homes
      .filter(h => h.latitude && h.longitude)
      .map(h => [parseFloat(h.latitude!), parseFloat(h.longitude!)] as [number, number]);
    if (coords.length) {
      map.fitBounds(coords, { padding: [40, 40], maxZoom: 17 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homes.length]);
  return null;
};

const HealthBadge = ({ health }: { health: HomeHealth | null | undefined }) => {
  if (!health) return <Chip icon={<HelpOutlineIcon />} label="Unknown" size="small" />;
  if (health.all_healthy) return <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" size="small" />;

  const failing = Object.entries(health.checks)
    .filter(([, v]) => v === false)
    .map(([k]) => k.replace(/_/g, ' ').toLowerCase());

  return (
    <Tooltip title={failing.join(', ')} placement="top">
      <Chip
        icon={<WarningAmberIcon />}
        label={`${failing.length} issue${failing.length > 1 ? 's' : ''}`}
        color="error"
        size="small"
      />
    </Tooltip>
  );
};

const PropertyPanel = ({
  property,
  health,
  onClose,
}: {
  property: Property;
  health: HomeHealth | null | undefined;
  onClose: () => void;
}) => (
  <Card elevation={0} sx={{ minWidth: 260 }}>
    <CardHeader
      title={property.name}
      subheader={property.physical_address?.address_line_1}
      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
      subheaderTypographyProps={{ variant: 'caption' }}
      action={
        <Stack direction="row" spacing={0}>
          <Tooltip title="Open home" placement="top">
            <IconButton
              size="small"
              component={Link}
              to={`/homes/${property.property_id}`}
              target="_blank"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
    />
    <Divider />
    <CardContent>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Health</Typography>
          <HealthBadge health={health} />
        </Stack>
        {property.plan_name && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Plan</Typography>
            <Typography variant="body2">{property.plan_name}</Typography>
          </Stack>
        )}
        <Divider />
        <MuiLink
          component={Link}
          to={`/homes/${property.property_id}/performance`}
          variant="body2"
          underline="hover"
        >
          View performance →
        </MuiLink>
        <MuiLink
          component={Link}
          to={`/homes/${property.property_id}/health`}
          variant="body2"
          underline="hover"
        >
          View health checks →
        </MuiLink>
      </Stack>
    </CardContent>
  </Card>
);

const NeighborhoodMap = () => {
  const neighborhood = useAppSelector((state: RootState) => state.neighborhood.neighborhood);
  const health = useAppSelector((state: RootState) => state.neighborhood.health);
  const allHomes = useAppSelector((state: RootState) => state.homes);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const neighborhoodHomes = useMemo(
    () => (allHomes as Property[]).filter(h => neighborhood?.property_ids?.includes(h.property_id)),
    [allHomes, neighborhood?.property_ids]
  );

  const homesWithCoords = useMemo(
    () => neighborhoodHomes.filter(h => h.latitude && h.longitude),
    [neighborhoodHomes]
  );

  const selectedHome = useMemo(
    () => neighborhoodHomes.find(h => h.property_id === selectedId) ?? null,
    [neighborhoodHomes, selectedId]
  );

  const healthySummary = useMemo(() => {
    if (!health) return null;
    const entries = Object.entries(health);
    const healthy = entries.filter(([, v]) => v?.all_healthy).length;
    return { healthy, total: entries.length };
  }, [health]);

  const handleMarkerClick = useCallback((propertyId: string) => {
    setSelectedId(prev => prev === propertyId ? null : propertyId);
  }, []);

  // Default center (NYC) — AutoBounds will override once homes load
  const defaultCenter: [number, number] = [40.73, -73.93];

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 130px)' }}>
      {/* MAP */}
      <Box sx={{ flex: 1, borderRadius: 1, overflow: 'hidden', minHeight: 400 }}>
        <MapContainer
          center={defaultCenter}
          zoom={13}
          style={{ height: '100%', width: '100%', background: '#1a1a2e' }}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            subdomains="abcd"
            maxZoom={20}
          />
          <AutoBounds homes={homesWithCoords} />
          {homesWithCoords.map(home => {
            const homeHealth = health?.[home.property_id];
            const color = markerColor(homeHealth);
            const isSelected = selectedId === home.property_id;
            return (
              <CircleMarker
                key={home.property_id}
                center={[parseFloat(home.latitude!), parseFloat(home.longitude!)]}
                radius={isSelected ? 14 : 10}
                pathOptions={{
                  color: isSelected ? '#fff' : color,
                  fillColor: color,
                  fillOpacity: 0.9,
                  weight: isSelected ? 3 : 1.5,
                }}
                eventHandlers={{ click: () => handleMarkerClick(home.property_id) }}
              >
                <Popup>
                  <Typography variant="body2" fontWeight={600}>{home.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {home.physical_address?.address_line_1}
                  </Typography>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Box>

      {/* SIDEBAR */}
      <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {/* Neighborhood summary */}
        <Card>
          <CardHeader
            title={neighborhood?.name || 'Neighborhood'}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Properties</Typography>
                <Typography variant="body2" fontWeight={600}>{neighborhoodHomes.length}</Typography>
              </Stack>
              {healthySummary && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Healthy</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {healthySummary.healthy === healthySummary.total
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <ErrorIcon fontSize="small" color="error" />
                    }
                    <Typography variant="body2" fontWeight={600}>
                      {healthySummary.healthy} / {healthySummary.total}
                    </Typography>
                  </Stack>
                </Stack>
              )}
              <Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: HEALTH_GOOD_COLOR }} />}
                  label="Healthy"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: HEALTH_BAD_COLOR }} />}
                  label="Issues"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: HEALTH_UNKNOWN_COLOR }} />}
                  label="Unknown"
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Selected property panel */}
        {selectedHome ? (
          <PropertyPanel
            property={selectedHome}
            health={health?.[selectedHome.property_id]}
            onClose={() => setSelectedId(null)}
          />
        ) : (
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack spacing={1} alignItems="center" sx={{ pt: 4, color: 'text.disabled' }}>
                <Typography variant="body2" textAlign="center">
                  Click a marker to see property details
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Property list */}
        {!selectedHome && neighborhoodHomes.length > 0 && (
          <Card>
            <CardHeader title="All Properties" titleTypographyProps={{ variant: 'subtitle2' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <Stack divider={<Divider />}>
                {neighborhoodHomes.map(home => {
                  const homeHealth = health?.[home.property_id];
                  return (
                    <Stack
                      key={home.property_id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => handleMarkerClick(home.property_id)}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{
                          width: 10, height: 10, borderRadius: '50%',
                          bgcolor: markerColor(homeHealth),
                          flexShrink: 0,
                        }} />
                        <Typography variant="body2">{home.name}</Typography>
                      </Stack>
                      {!homeHealth?.all_healthy && homeHealth && (
                        <WarningAmberIcon fontSize="small" color="error" />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default NeighborhoodMap;
